import React, { useState, useEffect } from 'react';
import { useMsal } from "@azure/msal-react";
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import '../styles/dashboard.css';

function Dashboard() {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} - ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  // Fetch user info when component mounts
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setIsLoading(true);
        const userId = accounts[0]?.localAccountId;

        if (!userId) {
          setIsLoading(false);
          navigate('/');
          return;
        }

        // Fetch user info
        const userResponse = await fetch(`/api/getUser?id=${encodeURIComponent(userId)}`);
        if (!userResponse.ok) {
          throw new Error('Failed to fetch user info');
        }
        
        const userData = await userResponse.json();
        setUserInfo(userData);
        setProjects(userData.projects || []);

        // Get SAS token using user ID as container name
        const sasResponse = await fetch(`/api/generateSasToken?container=${encodeURIComponent(userId)}`);
        if (sasResponse.ok) {
          const { sasToken, containerName, storageAccountName } = await sasResponse.json();
          
          // Store in sessionStorage
          sessionStorage.setItem('sasToken', sasToken);
          sessionStorage.setItem('containerName', containerName);
          sessionStorage.setItem('storageAccountName', storageAccountName);

          console.log(sasToken, containerName, storageAccountName);
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    if (accounts.length > 0) {
      fetchUserInfo();
    } else {
      setIsLoading(false);
      navigate('/');
    }
  }, [accounts, navigate]);

  const handleDeleteProject = async (projectId) => {
    if (!userInfo?.id) return;

    try {
      const response = await fetch(`/api/updateUser/${userInfo.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'deleteProject',
          data: {
            projectId
          },
        }),
      });

      if (!response.ok) throw new Error('Failed to delete project');
      
      const updatedUser = await response.json();
      setProjects(updatedUser.projects || []);
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const confirmDelete = (projectId, projectName) => {
    if (window.confirm(`Are you sure you want to permanently delete "${projectName}"? This action cannot be undone.`)) {
      handleDeleteProject(projectId);
    }
  };

  const handleLogout = async () => {
    await instance.logoutRedirect();
  };

  const handleEditProject = (projectId) => {
    navigate(`/project?project=${projectId}`);
  };

  const handleNewProject = () => {
    navigate('/new-project');
  };

  return (
    <div className="container">
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}

      <nav className="nav">
        <img src={logo} alt="ExcelEA Logo" className="logo" />
        <div className="user-section">
          <span className="username">
            Welcome, {userInfo?.name || 'User'}
          </span>
          <button onClick={handleLogout} className="logout-button">
            Sign Out
          </button>
        </div>
      </nav>

      <div className="content">
        <div className="header">
          <div>
            <h1 className="title">Enterprise Architecture Projects</h1>
            <p className="subtitle">AI-powered assistant for enterprise architecture design and documentation</p>
          </div>
          <button 
            onClick={handleNewProject} 
            className="new-project-button"
          >
            New Project
          </button>
        </div>

        <div className="projects-grid">
          {projects.map(project => (
            <div key={project.id} className="project-card">
              <div className="project-header">
                <h3 className="project-name">{project.name}</h3>
              </div>
              <p className="project-description">{project.description}</p>
              <p className="project-date">
                Last modified: {formatDateTime(project.updatedAt)}
              </p>
              <div className="project-actions">
                <button 
                  onClick={() => handleEditProject(project.id)}
                  className="edit-button"
                >
                  View Design
                </button>
                <button 
                  onClick={() => confirmDelete(project.id, project.name)}
                  className="delete-button"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Dashboard; 